import type { KyThuNhap } from '@qlk/shared';
import {
  LOAI_THUE_TNCN_LABEL,
  TRANG_THAI_DOI_SOAT_LABEL,
  dinhDangVnd,
  ngayDayVn,
  nhanKyDoiSoat,
} from '@qlk/shared';
import { Link } from 'react-router-dom';

import { useKyChung } from '../../../layout/kyChung.js';
import {
  DanhSachSkeleton,
  DataTable,
  EmptyState,
  Nut,
  PageHeader,
  StatStrip,
  StatusBadge,
  dinhDangTien,
  type CotBang,
  type OChiSo,
  type Tone,
} from '../../../ui/index.js';
import { useThuNhapCuaToi } from '../api.js';

const TONE: Record<string, Tone> = {
  nhap: 'neutral',
  da_chot: 'info',
  da_thanh_toan: 'ok',
};

/** `2026-10` → `tháng 10/2026`. */
function nhanThang(period: string): string {
  const [nam, thang] = period.split('-');
  return `tháng ${thang ?? '?'}/${nam ?? '?'}`;
}

/**
 * "Thu nhập của tôi" — mỗi người xem phần của **chính mình**.
 *
 * Hai luật nhìn thấy ngay trên trang:
 *
 * - Chỉ **phiên bản đang hiệu lực**. Bản đã bị thay không phải số của họ nữa,
 *   nên nó không xuất hiện ở đây (quản trị vẫn in lại được từ trang Đối soát).
 * - Kỳ còn **nháp** không hiện số. Số chưa chốt chưa phải lời hứa trả tiền;
 *   hiện ra là mời người ta hỏi "sao tháng này tụt" về một con số còn đang đổi.
 *
 * ## Đổi ở bước 3/3
 *
 * Ô chọn kỳ chung ở thanh trên đổi **dải chỉ số** (kỳ đó tôi nhận bao nhiêu),
 * còn bảng vẫn liệt kê **mọi kỳ** — chỗ người ta so tháng này với tháng trước.
 * Lọc bảng theo một kỳ thì mất đúng cái khiến trang này đáng mở.
 */
export function TrangThuNhapCuaToi() {
  const ky = useKyChung();
  /* Gọi **không** kèm `period`: trang cần cả lịch sử, kỳ đang chọn lấy từ đó. */
  const thuNhap = useThuNhapCuaToi();

  if (thuNhap.isError) {
    return (
      <>
        <PageHeader title="Thu nhập của tôi" />
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được thu nhập"
            description={thuNhap.error.message}
            action={
              <Nut
                kieu="phu"
                onClick={() => {
                  void thuNhap.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      </>
    );
  }

  if (thuNhap.isPending || !thuNhap.data) {
    return (
      <>
        <PageHeader title="Thu nhập của tôi" />
        <DanhSachSkeleton />
      </>
    );
  }

  const d = thuNhap.data;
  const kyChon = d.ky.find((k) => k.period === ky.giaTri) ?? null;
  const daTra = d.ky.filter((k) => k.trangThai === 'da_thanh_toan').length;

  /** Số của kỳ đang chọn, hoặc câu nói rõ vì sao chưa có số. */
  const soCuaKy = (lay: (k: KyThuNhap) => string): string => {
    if (!kyChon) return '—';
    if (!kyChon.coSo) return '—';
    return lay(kyChon);
  };

  const phuCuaKy = kyChon
    ? kyChon.coSo
      ? TRANG_THAI_DOI_SOAT_LABEL[kyChon.trangThai].toLowerCase()
      : 'đang tính, chưa chốt'
    : 'kỳ này không có phần chia nào của bạn';

  const o: OChiSo[] = [
    {
      nhan: `Phần chia · ${nhanThang(ky.giaTri)}`,
      giaTri: soCuaKy((k) => dinhDangTien(k.phaiTra ?? 0)),
      phu: phuCuaKy,
    },
    {
      nhan: `Thực chuyển · ${nhanThang(ky.giaTri)}`,
      giaTri: soCuaKy((k) => (k.thucChuyenVnd === null ? '—' : dinhDangVnd(k.thucChuyenVnd))),
      phu: kyChon?.thanhToanNgay ? `trả ${ngayDayVn(kyChon.thanhToanNgay)}` : 'chưa chuyển',
    },
    {
      nhan: 'Đã nhận · mọi kỳ',
      giaTri: dinhDangVnd(d.tongDaTraVnd),
      phu: `${String(daTra)} kỳ đã thanh toán`,
    },
    {
      nhan: 'Thuế TNCN đã khấu trừ · mọi kỳ',
      giaTri: dinhDangVnd(d.tongThueVnd),
      phu: d.hoSo ? LOAI_THUE_TNCN_LABEL[d.hoSo.loaiThue] : 'chưa khai hồ sơ thuế',
    },
    {
      nhan: 'Hồ sơ thuế · hiện tại',
      giaTri: d.hoSo ? d.hoSo.hoTenDayDu || d.tenNguoi : 'Chưa khai',
      phu: d.hoSo
        ? `CCCD ${d.hoSo.cccd || '—'} · TK ${d.hoSo.soTaiKhoan || '—'}`
        : 'nhờ quản trị nhân sự khai hồ sơ để in biên bản',
    },
  ];

  const cot: CotBang<KyThuNhap>[] = [
    {
      key: 'ky',
      header: 'Kỳ',
      render: (k) => (
        <span className="font-medium text-chu">
          {nhanKyDoiSoat(k.period)}
          {k.period === ky.giaTri && (
            <span className="ml-2 text-phu font-normal text-nhan">đang xem</span>
          )}
        </span>
      ),
    },
    {
      key: 'trang-thai',
      header: 'Trạng thái',
      render: (k) => (
        <StatusBadge tone={TONE[k.trangThai] ?? 'neutral'}>
          {TRANG_THAI_DOI_SOAT_LABEL[k.trangThai]}
        </StatusBadge>
      ),
    },
    {
      key: 'so-kenh',
      header: 'Kênh',
      align: 'right',
      render: (k) => (k.coSo ? k.soKenh : null),
    },
    {
      key: 'phan-chia',
      header: 'Phần chia (USD)',
      align: 'right',
      render: (k) =>
        k.coSo ? (
          <>
            {dinhDangTien(k.phaiTra ?? 0)}
            {k.dieuChinh !== null && k.dieuChinh !== 0 && (
              <span className="block text-phu text-chu-phu">
                gồm điều chỉnh {dinhDangTien(k.dieuChinh)}
              </span>
            )}
          </>
        ) : (
          /* Kỳ còn nháp: nói thẳng vì sao trống, không để ô rỗng. */
          <span className="text-phu text-chu-phu">đang tính</span>
        ),
    },
    {
      key: 'vnd',
      header: 'Phải trả (VND)',
      align: 'right',
      render: (k) =>
        !k.coSo ? null : k.phaiTraVnd === null ? (
          <span className="text-phu text-chu-phu">chờ tỷ giá</span>
        ) : (
          dinhDangVnd(k.phaiTraVnd)
        ),
    },
    {
      key: 'thue',
      header: 'Thuế TNCN',
      align: 'right',
      render: (k) => (k.coSo && k.thueTncn !== null ? dinhDangVnd(k.thueTncn) : null),
    },
    {
      key: 'thuc-chuyen',
      header: 'Thực chuyển',
      align: 'right',
      render: (k) =>
        k.coSo && k.thucChuyenVnd !== null ? (
          <span className="font-semibold">{dinhDangVnd(k.thucChuyenVnd)}</span>
        ) : null,
    },
    {
      key: 'ngay-tra',
      header: 'Ngày trả',
      render: (k) => (k.thanhToanNgay ? ngayDayVn(k.thanhToanNgay) : null),
    },
    {
      key: 'bien-ban',
      header: <span className="sr-only">Biên bản</span>,
      align: 'right',
      render: (k) =>
        k.payoutId ? (
          <Link to={`/bien-ban/${k.payoutId}`}>
            <Nut kieu="phu" co="nho">
              Biên bản
            </Nut>
          </Link>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Thu nhập của tôi"
        phu="Phần chia của bạn theo từng kỳ — chỉ kỳ đã chốt mới có số"
        giaiThich="Trang này chỉ hiện phiên bản đang hiệu lực của từng kỳ. Có thắc mắc về một con số thì liên hệ quản trị nhân sự — họ xem được cả lịch sử chốt."
      />

      <div className="mb-4">
        <StatStrip aria-label={`Thu nhập ${nhanThang(ky.giaTri)}`} o={o} />
      </div>

      <DataTable
        caption="Thu nhập theo từng kỳ"
        columns={cot}
        rows={d.ky}
        keyOf={(k) => k.period}
        tongSo={d.ky.length}
        khiRong={
          <EmptyState
            title="Chưa có kỳ nào được chốt"
            description="Phần chia hiện ở đây sau khi quản trị nhân sự chốt kỳ. Kỳ đang tính chưa có số."
          />
        }
      />
    </>
  );
}
